import React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

export interface CookieConsent {
  necessary: boolean;
  analytics?: boolean;
}

interface CookieConsentContextType {
  consent: CookieConsent | null;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: CookieConsent) => void;
  hasConsent: (category: keyof CookieConsent) => boolean;
  resetConsent: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

const CONSENT_STORAGE_KEY = 'cookie_consent';
const CONSENT_VERSION = '1.0';
const CONSENT_EXPIRY_DAYS = 90;

interface StoredConsent {
  consent: CookieConsent;
  version: string;
  timestamp: number;
}

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const checkExistingConsent = () => {
      try {
        const storedData = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (storedData) {
          const parsed: StoredConsent = JSON.parse(storedData);
          
          const now = Date.now();
          const expiryTime = parsed.timestamp + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
          
          if (now < expiryTime && parsed.version === CONSENT_VERSION) {
            setConsent(parsed.consent);
            setShowBanner(false);
          } else {
            localStorage.removeItem(CONSENT_STORAGE_KEY);
            setShowBanner(true);
          }
        } else {
          setShowBanner(true);
        }
      } catch (error) {
        setShowBanner(true);
      }
    };

    checkExistingConsent();
  }, []);

  const saveConsent = (newConsent: CookieConsent) => {
    const consentData: StoredConsent = {
      consent: newConsent,
      version: CONSENT_VERSION,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
      setConsent(newConsent);
      setShowBanner(false);
    } catch (error) {
      console.error('Error saving cookie consent');
    }
  };

  const acceptAll = () => {
    const consent: CookieConsent = {
      necessary: true,
      analytics: true
    };
    saveConsent(consent);
  };

  const rejectAll = () => {
    const consent: CookieConsent = {
      necessary: true,
      analytics: false
    };
    saveConsent(consent);
  };

  const savePreferences = (preferences: CookieConsent) => {
    saveConsent(preferences);
  };

  const hasConsent = (category: keyof CookieConsent): boolean => {
    return consent ? consent[category] || false : false;
  };

  const resetConsent = () => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      setConsent(null);
      setShowBanner(true);
    } catch (error) {
      console.error('Error resetting cookie consent');
    }
  };

  const value: CookieConsentContextType = {
    consent,
    showBanner,
    acceptAll,
    rejectAll,
    savePreferences,
    hasConsent,
    resetConsent
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};